import { BarChart, DonutChart } from '@mantine/charts';
import { Skeleton } from '@mantine/core';
import { IconPackages } from '@tabler/icons-react';
import DialogBox from '@xver/components/Atoms/DialogBox/DialogBox';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatBytes } from '@xver/utils/units';

import { usePricingQuery } from '@xver/hooks/usePricingQuery';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../../store';
import { useProjectQuery } from '../hooks/useProjectQuery';
import { useRepositoriesQuery } from '../hooks/useRepositoriesQuery';

interface OverviewTabPanelProps {
    projectIdOrName: string;
}

export default function OverviewTabPanel({ projectIdOrName }: OverviewTabPanelProps) {
    const { t } = useTranslation('artifactRegistry');

    const user = useSelector((state: ApplicationState) => state.auth.user);

    const { data: project, isPending: isProjectPending } = useProjectQuery(projectIdOrName);
    const { data: repositoriesData, isPending: isReposPending } =
        useRepositoriesQuery(projectIdOrName);

    const { data: pricingResp } = usePricingQuery();
    const registryFreeGb = String(pricingResp?.products.registry.free_storage_gb ?? 0.5);
    const registryRate = String(pricingResp?.products.registry.gb_hourly_rate ?? 1.5);

    const repositories = repositoriesData?.repositories ?? [];
    const totalPullCount = useMemo(
        () => repositories.reduce((sum, r) => sum + (r.pull_count ?? 0), 0),
        [repositories]
    );

    const donutData = useMemo(() => {
        const used = project?.storage_used ?? 0;
        const quota = project?.quota ?? 0;
        if (quota > 0) {
            const remaining = Math.max(quota - used, 0);
            return [
                { name: t('overview.donut.used', 'Used'), value: used, color: 'blue.6' },
                {
                    name: t('overview.donut.remaining', 'Remaining'),
                    value: remaining,
                    color: 'gray.7',
                },
            ];
        }
        return [{ name: t('overview.donut.empty', 'No quota'), value: 1, color: 'gray.7' }];
    }, [project, t]);

    const barData = useMemo(() => {
        return [...repositories]
            .sort((a, b) => (b.pull_count ?? 0) - (a.pull_count ?? 0))
            .slice(0, 10)
            .map((repo) => ({ name: repo.name, pulls: repo.pull_count ?? 0 }));
    }, [repositories]);

    const isLoading = isProjectPending || isReposPending;
    const hasNoData = !isLoading && !project && repositories.length === 0;

    if (hasNoData) {
        return (
            <DialogBox
                label={t('overview.empty.title', 'No data available')}
                description={t(
                    'overview.empty.description',
                    'Push your first image to populate this overview.'
                )}
                classNames="p-12 items-center justify-center"
            >
                <div className="w-full flex flex-col items-center justify-center gap-3 py-8">
                    <IconPackages size={48} stroke={1.25} className="text-fg/40" />
                    <p className="text-sm text-fg/68">
                        {t(
                            'overview.empty.message',
                            'No repositories yet. Push an image to your project to see stats here.'
                        )}
                    </p>
                </div>
            </DialogBox>
        );
    }

    return (
        <div className="w-full flex flex-col gap-6">
            <div className="w-full grid grid-cols-1 xl:grid-cols-4 border-[0.5px] border-line rounded-sm">
                <div className="w-full h-auto p-4 flex flex-col justify-start items-start gap-1 border-b-[0.5px] border-line xl:border-b-0 bg-surface-1 rounded-none xl:rounded-l-sm">
                    <h2 className="text-base text-fg/68">
                        {t('overview.stats.project', 'Project')}
                    </h2>
                    {project && !isProjectPending ? (
                        <div className="w-full flex flex-col gap-1">
                            <span className="text-xl font-bold truncate">{project.name}</span>
                            <span className="text-sm text-fg/68 truncate">
                                {t('overview.stats.owner', 'Owner')}: {user?.email}
                            </span>
                        </div>
                    ) : (
                        <Skeleton height={31} width={'100%'} radius="sm" />
                    )}
                </div>
                <div className="w-full h-auto p-4 flex flex-col justify-start items-start gap-1 bg-surface-1 border-b-[0.5px] border-l-0 xl:border-l-[0.5px] xl:border-b-0 border-line">
                    <h2 className="text-base text-fg/68">
                        {t('overview.stats.repositories', 'Repositories')}
                    </h2>
                    {project && !isProjectPending ? (
                        <div className="inline-flex items-center gap-1">
                            <span className="text-2xl font-bold">{project.repository_count}</span>
                            <span className="text-base text-fg/68">
                                {t('overview.stats.repositoriesSuffix', 'repos')}
                            </span>
                        </div>
                    ) : (
                        <Skeleton height={31} width={'100%'} radius="sm" />
                    )}
                </div>
                <div className="w-full h-auto p-4 flex flex-col justify-start items-start gap-1 bg-surface-1 border-b-[0.5px] border-l-0 xl:border-l-[0.5px] xl:border-b-0 border-line">
                    <h2 className="text-base text-fg/68">
                        {t('overview.stats.quota', 'Storage quota')}
                    </h2>
                    {project && !isProjectPending ? (
                        <div className="inline-flex items-center gap-1">
                            <span className="text-2xl font-bold">
                                {formatBytes(project.storage_used)}
                            </span>
                        </div>
                    ) : (
                        <Skeleton height={31} width={'100%'} radius="sm" />
                    )}
                </div>
                <div className="w-full h-auto p-4 flex flex-col justify-start items-start gap-1 bg-surface-1 border-l-0 xl:border-l-[0.5px] border-line rounded-r-sm">
                    <h2 className="text-base text-fg/68">
                        {t('overview.stats.totalPulls', 'Total pull count')}
                    </h2>
                    {!isReposPending ? (
                        <div className="inline-flex items-center gap-1">
                            <span className="text-2xl font-bold">
                                {totalPullCount.toLocaleString()}
                            </span>
                            <span className="text-base text-fg/68">
                                {t('overview.stats.totalPullsSuffix', 'pulls')}
                            </span>
                        </div>
                    ) : (
                        <Skeleton height={31} width={'100%'} radius="sm" />
                    )}
                </div>
            </div>

            <div className="w-full grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-4">
                <DialogBox
                    label={t('overview.charts.donutTitle', 'Repository usage')}
                    description={t(
                        'overview.charts.donutDescription',
                        'Repositories against project quota.'
                    )}
                    classNames="p-6 items-center justify-center"
                >
                    {isProjectPending ? (
                        <Skeleton height={240} width="100%" radius="sm" />
                    ) : (
                        <div className="w-full flex flex-col items-center gap-4">
                            <DonutChart
                                data={donutData}
                                size={220}
                                thickness={28}
                                withLabelsLine
                                withLabels
                                chartLabel={formatBytes(project?.storage_used ?? 0)}
                                paddingAngle={2}
                                valueFormatter={(value) => formatBytes(value)}
                            />
                            <div className="w-full flex flex-col gap-1.5">
                                {donutData.map((d) => (
                                    <div
                                        key={d.name}
                                        className="w-full flex flex-row items-center justify-between text-sm"
                                    >
                                        <div className="inline-flex items-center gap-2">
                                            <span
                                                className="w-2.5 h-2.5 rounded-full"
                                                style={{
                                                    backgroundColor: `var(--mantine-color-${d.color.replace('.', '-')})`,
                                                }}
                                            />
                                            <span className="text-fg/78">{d.name}</span>
                                        </div>
                                        <span className="text-fg/68">{formatBytes(d.value)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </DialogBox>

                <DialogBox
                    label={t('overview.charts.barTitle', 'Top repositories by pulls')}
                    description={t('overview.charts.barDescription', 'Ranked by pull count.')}
                    classNames="py-4 flex flex-col justify-center items-center"
                >
                    {isReposPending ? (
                        <Skeleton height={240} width="100%" radius="sm" />
                    ) : barData.length === 0 ? (
                        <div className="w-full flex flex-col items-center justify-center gap-2 py-36.5">
                            <IconPackages size={32} stroke={1.25} className="text-fg/40" />
                            <span className="text-sm text-fg/68">
                                {t(
                                    'overview.charts.barEmpty',
                                    'No pull activity yet. Pull an image to see stats here.'
                                )}
                            </span>
                        </div>
                    ) : (
                        <BarChart
                            h={378}
                            data={barData}
                            dataKey="name"
                            series={[
                                {
                                    name: 'pulls',
                                    label: t('overview.charts.pulls', 'Pulls'),
                                    color: 'blue.6',
                                },
                            ]}
                            withLegend={false}
                            tickLine="y"
                            gridAxis="y"
                        />
                    )}
                </DialogBox>
            </div>

            <DialogBox
                label={t('overview.pricing.title', 'How storage is billed')}
                description={t(
                    'overview.pricing.description',
                    'Storage usage is metered hourly per project.'
                )}
                classNames="p-4"
            >
                <ul className="list-disc list-inside flex flex-col gap-1.5 text-sm text-fg/68">
                    <li>
                        {t(
                            'overview.pricing.freeTier',
                            'First {{free}} GB free every month, applied automatically.',
                            { free: registryFreeGb }
                        )}
                    </li>
                    <li>
                        {t(
                            'overview.pricing.rate',
                            '{{rate}} ฿ per GB-hour for storage above the free tier.',
                            { rate: registryRate }
                        )}
                    </li>
                    <li>
                        {t(
                            'overview.pricing.calculation',
                            'Hourly charge = (used GB − {{free}}) × {{rate}} ฿, deducted from your credit balance.',
                            { free: registryFreeGb, rate: registryRate }
                        )}
                    </li>
                    <li>
                        {t(
                            'overview.pricing.insufficientFunds',
                            "If your balance can't cover the charge, the registry is suspended and deleted after a 24-hour grace period."
                        )}
                    </li>
                </ul>
            </DialogBox>
        </div>
    );
}
